<template>
  <div class="wa-settings">
    <header class="wa-settings__header">
      <div>
        <h2>WhatsApp Bot Einstellungen</h2>
        <p>
          Leere Felder verwenden automatisch den Standardtext.
          ?nderungen gelten f?r neue Bot-Nachrichten.
        </p>
      </div>
      <div class="wa-settings__actions">
        <button type="button" class="secondary" @click="clearAll">
          Alle Overrides leeren
        </button>
        <button type="button" :disabled="busy" @click="save">
          Speichern
        </button>
      </div>
    </header>

    <nav class="wa-settings__tabs">
      <button
        v-for="section in sections"
        :key="section.key"
        type="button"
        :class="{active: activeSection === section.key}"
        @click="activeSection = section.key"
      >
        {{ section.title }} ({{ section.keys.length }})
      </button>
    </nav>

    <div v-if="loading" class="wa-settings__empty">Einstellungen werden geladen?</div>

    <section v-else class="wa-settings__fields">
      <article v-for="key in activeKeys" :key="key" class="wa-setting">
        <div class="wa-setting__title">
          <div>
            <strong>{{ labels[key] || key }}</strong>
            <code>{{ key }}</code>
          </div>
          <button type="button" class="link" @click="values[key] = ''">
            Standard verwenden
          </button>
        </div>

        <textarea
          v-model="values[key]"
          :rows="key === 'ai.system_prompt' ? 16 : 5"
          :placeholder="defaults[key]"
        />

        <details>
          <summary>Standardtext und verf?gbare Platzhalter</summary>
          <pre>{{ defaults[key] }}</pre>
          <small v-if="placeholders(key)">
            Platzhalter: {{ placeholders(key) }}
          </small>
        </details>
      </article>
    </section>

    <p v-if="notice" class="wa-settings__notice">{{ notice }}</p>
    <p v-if="error" class="wa-settings__error">{{ error }}</p>
  </div>
</template>

<script>
import authRequired from '@/mixins/auth_required'

export default {
  name: 'whatsapp-settings',
  mixins: [authRequired],
  data: () => ({
    loading: true,
    busy: false,
    activeSection: 'ai',
    values: {},
    defaults: {},
    labels: {},
    notice: '',
    error: ''
  }),
  computed: {
    sections () {
      const keys = Object.keys(this.defaults)
      return [
        {
          key: 'ai',
          title: 'AI',
          keys: keys.filter(key => key.startsWith('ai.'))
        },
        {
          key: 'service',
          title: 'Bot-Nachrichten',
          keys: keys.filter(
            key => key.startsWith('service.') ||
              key.startsWith('validation.')
          )
        },
        {
          key: 'questionnaire',
          title: 'Fragebogen',
          keys: keys.filter(key => key.startsWith('questionnaire.'))
        }
      ]
    },
    activeKeys () {
      const section = this.sections.find(item => item.key === this.activeSection)
      return section ? section.keys : []
    }
  },
  async mounted () {
    await this.load()
  },
  methods: {
    async load () {
      this.loading = true
      this.error = ''
      try {
        const result = await this.$services.whatsapp.getBotSettings()
        this.defaults = result.defaults || {}
        this.labels = result.labels || {}
        this.values = Object.keys(this.defaults).reduce((output, key) => {
          output[key] = (result.values || {})[key] || ''
          return output
        }, {})
      } catch (error) {
        this.setError(error)
      } finally {
        this.loading = false
      }
    },
    async save () {
      this.busy = true
      this.error = ''
      this.notice = ''
      try {
        const result = await this.$services.whatsapp.updateBotSettings(this.values)
        this.values = Object.keys(this.defaults).reduce((output, key) => {
          output[key] = (result.values || {})[key] || ''
          return output
        }, {})
        this.notice = 'Einstellungen wurden gespeichert.'
      } catch (error) {
        this.setError(error)
      } finally {
        this.busy = false
      }
    },
    clearAll () {
      if (!window.confirm('Alle eigenen Texte leeren und Standards verwenden?')) return
      Object.keys(this.values).forEach(key => { this.values[key] = '' })
    },
    placeholders (key) {
      return ({
        'service.order_created': '{order_id}',
        'service.reuse_summary': '{name}, {email}, {phone}, {address}',
        'service.payment_link': '{payment_url}',
        'questionnaire.phone_confirm': '{contact_phone}',
        'questionnaire.confirm': '{default_text}'
      })[key] || ''
    },
    setError (error) {
      this.error = error && error.message ? error.message : 'Aktion fehlgeschlagen.'
    }
  }
}
</script>

<style lang="stylus" scoped>
.wa-settings
  box-sizing border-box
  min-height calc(100vh - 92px)
  padding 22px
  background #f4f6f8
  color #263238
  &__header
    display flex
    justify-content space-between
    gap 24px
    align-items flex-start
    h2
      margin 0 0 6px
    p
      margin 0
      color #617078
  &__actions
    display flex
    gap 8px
    button
      padding 9px 14px
      border 0
      background #167b55
      color white
      cursor pointer
    .secondary
      background #e4e8ea
      color #263238
  &__tabs
    display flex
    gap 8px
    margin 22px 0 14px
    button
      padding 9px 14px
      border 1px solid #ccd4d8
      background white
      cursor pointer
      &.active
        border-color #167b55
        background #e9f7ef
        color #11643f
  &__fields
    display grid
    gap 14px
  &__notice
    color #167b55
  &__error
    color #b42318
  &__empty
    padding 30px
    background white
.wa-setting
  padding 16px
  border 1px solid #dfe4e7
  background white
  &__title
    display flex
    justify-content space-between
    gap 20px
    margin-bottom 10px
    strong, code
      display block
    code
      margin-top 4px
      color #77858c
    .link
      border 0
      background transparent
      color #167b55
      cursor pointer
  textarea
    box-sizing border-box
    width 100%
    resize vertical
    font-family inherit
    line-height 1.4
  details
    margin-top 9px
    color #617078
    summary
      cursor pointer
    pre
      white-space pre-wrap
      padding 10px
      background #f7f8f9
      color #263238
@media (max-width: 850px)
  .wa-settings__header
    flex-direction column

